/**
 * WebSocket sync service for Taitora canvas.
 *
 * Authentication flow (ticket exchange):
 *  1. connect(forgeId) calls POST /api/v1/sync/tickets with the Clerk JWT.
 *  2. Backend returns a 10-second single-use ticket.
 *  3. WS opens to /api/v1/sync/ws?forge_id=…&ticket=… — no JWT in URL.
 *
 * If ticket fetch fails the WS is not opened; onAuthError callback fires.
 *
 * Reconnect: exponential backoff (1 s → 2 → 4 → … → 30 s).
 * Outbound messages are queued while disconnected and flushed on open.
 */

import { apiUrl, wsUrl } from "@/config/api";
import { tracedFetch } from "@/utils/tracedFetch";

const WS_PATH = "/api/v1/sync/ws";
const TICKET_PATH = "/api/v1/sync/tickets";
const BACKOFF_BASE = 1_000;
const BACKOFF_MAX = 30_000;

// ── Wire-protocol types ───────────────────────────────────────────────────────

// Mirrors the Go constants in internal/realtime/events.go. Adding one there means
// adding a case to useCanvasStore.applyWsMessage, or the frame arrives and is
// silently dropped.
export type WsEventType =
  | "node_moved"
  | "shape_moved"
  | "node_added"
  | "node_deleted"
  | "edge_added"
  | "edge_deleted"
  | "shape_added"
  | "shape_updated"
  | "page_added"
  | "card_updated"
  | "canvas_sync"
  | "canvas_revision";

export interface WsMessage<T = unknown> {
  type: WsEventType;
  forge_id: string;
  payload: T;
  /** Forge revision after this change — the save base a client adopts once it
   *  has applied the patch. Absent on peer-relayed frames, which carry no
   *  server revision; absent is not 0, and a client must leave its base alone
   *  rather than reset it. */
  revision?: number;
}

/** `canvas_revision` — someone else's full-canvas save. No entities: the
 *  recipient refetches and reconciles. */
export interface CanvasRevisionPayload {
  revision: number;
}

export interface NodeMovedPayload {
  id: string;
  x: number;
  y: number;
}

export interface DeletedPayload {
  id: string;
}

/**
 * A shape's geometry after a drag — whole-shape move, endpoint drag, or resize.
 *
 * One event rather than three: all three gestures answer the same question for
 * the receiving canvas ("where is this shape now"), and the fields not touched
 * by a given gesture simply come back unchanged. Peer-relayed like node_moved,
 * so it carries no server revision.
 *
 * x2/y2 travel with every frame for line-like shapes. A line drag translates
 * both endpoints together, and sending only (x, y) would collapse the line on
 * the far side.
 */
export interface ShapeMovedPayload {
  id: string;
  x: number;
  y: number;
  x2?: number;
  y2?: number;
  width?: number;
  height?: number;
}

// Entity payloads (`node_added`, `edge_added`, `shape_added`) are the wire
// types in canvasWire.ts — the backend publishes exactly the JSON that
// GET /canvas returns, so both paths share one set of converters. `page_added`
// carries a NodePage (nodePageService.ts), likewise byte-identical to the
// GET /api/nodes/{id}/pages elements.
export type {
  WireNode as NodeAddedPayload,
  WireEdge as EdgeAddedPayload,
  WireShape as ShapeAddedPayload,
} from "./canvasWire";
export type { NodePage as PageAddedPayload } from "./nodePageService";

type MessageHandler = (msg: WsMessage) => void;
type AuthErrorHandler = (reason: string) => void;

/**
 * Supplies a *fresh* Clerk JWT. It must be the getter, not a token string:
 * Clerk session tokens expire after about a minute, so a token captured once at
 * connect time is already stale by the first reconnect — the ticket request then
 * fails with "invalid or expired token" and the socket never opens again.
 */
export type TokenSource = () => Promise<string | null>;

// ── Ticket fetch ─────────────────────────────────────────────────────────────

async function fetchTicket(authToken: string): Promise<string> {
  const resp = await tracedFetch(apiUrl(TICKET_PATH), {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${authToken}`,
      "Content-Type": "application/json",
    },
  });

  if (!resp.ok) {
    const body = await resp.text();
    throw new Error(`ticket fetch failed ${resp.status}: ${body}`);
  }

  const data = (await resp.json()) as { ticket: string; expires_in: number };
  if (!data.ticket) throw new Error("ticket response missing ticket field");
  return data.ticket;
}

// ── Manager ───────────────────────────────────────────────────────────────────

class WebSocketManager {
  private socket: WebSocket | null = null;
  private forgeId: string | null = null;
  private getToken: TokenSource | null = null;
  private retryCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private queue: WsMessage[] = [];
  private handlers = new Set<MessageHandler>();
  private authErrorHandlers = new Set<AuthErrorHandler>();
  private reopenHandlers = new Set<() => void>();
  private closed = false;
  // Whether this connect() has ever reached an open socket. Distinguishes the
  // first open (the caller just loaded the canvas, nothing to resync) from a
  // reopen after a drop (frames were missed and nothing replays them).
  private hasOpened = false;

  /**
   * Open a WS connection for the given forge.
   * getToken: supplies a Clerk session JWT per attempt — used only to fetch the
   *           short-lived ticket, never sent in the WS URL.
   */
  async connect(forgeId: string, getToken: TokenSource): Promise<void> {
    this.closed = false;
    this.forgeId = forgeId;
    this.getToken = getToken;
    this.retryCount = 0;
    this.hasOpened = false;
    await this._open();
  }

  /** Intentional close — suppresses reconnect. */
  disconnect(): void {
    this.closed = true;
    this._clearTimer();
    if (this.socket) {
      this.socket.close(1000, "client disconnect");
      this.socket = null;
    }
  }

  send<T>(msg: WsMessage<T>): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(msg));
    } else {
      this.queue.push(msg as WsMessage);
    }
  }

  addHandler(h: MessageHandler): void {
    this.handlers.add(h);
  }

  removeHandler(h: MessageHandler): void {
    this.handlers.delete(h);
  }

  /** Called when ticket fetch fails or JWT is rejected at the edge. */
  onAuthError(h: AuthErrorHandler): void {
    this.authErrorHandlers.add(h);
  }

  /**
   * Called when the socket comes back after a drop — never on the first open.
   *
   * Nothing replays frames sent while the socket was down, so a reconnect is
   * the one moment a client knows it may have missed changes without knowing
   * which. It matters more here than it looks: the Go API runs in a Cloudflare
   * container with `sleepAfter = "5m"`, so an idle forge tears down every
   * socket on a timer, not just on deploys.
   *
   * Paired with a remover, unlike onAuthError — AppShell re-registers on every
   * forge switch, and an add-only set would accumulate a resync per switch.
   */
  onReopen(h: () => void): void {
    this.reopenHandlers.add(h);
  }

  offReopen(h: () => void): void {
    this.reopenHandlers.delete(h);
  }

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  private async _open(): Promise<void> {
    if (!this.forgeId || !this.getToken || this.closed) return;

    let ticket: string;
    try {
      const token = await this.getToken();
      if (!token) throw new Error("no auth token available");
      ticket = await fetchTicket(token);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.authErrorHandlers.forEach((h) => h(msg));
      // Retry with backoff rather than giving up. The common failure is an
      // expired token, which the next attempt re-fetches; without this the
      // socket stayed dead for the life of the page and canvas sync silently
      // never happened. Backoff caps at 30s, so a genuinely bad credential
      // costs one request every 30s rather than a hot loop.
      this._scheduleReconnect();
      return;
    }

    const url = new URL(wsUrl(WS_PATH));
    url.searchParams.set("forge_id", this.forgeId);
    url.searchParams.set("ticket", ticket);

    const ws = new WebSocket(url.toString());
    this.socket = ws;

    ws.onopen = () => {
      this.retryCount = 0;
      this._drain();
      if (this.hasOpened) {
        this.reopenHandlers.forEach((h) => h());
      } else {
        this.hasOpened = true;
      }
    };

    // One WebSocket message may carry SEVERAL frames: the Go writePump batches
    // everything queued behind a slow client (e.g. a backgrounded tab) into a
    // single message, newline-separated (internal/realtime/handler.go). Frames
    // are single-line JSON, so splitting on \n is exact. Parsing the whole
    // message as one JSON silently lost every batched frame — and the autosave
    // full-replace then deleted those entities from the database.
    ws.onmessage = (e: MessageEvent<string>) => {
      for (const line of e.data.split("\n")) {
        if (line === "") continue;
        try {
          const msg = JSON.parse(line) as WsMessage;
          this.handlers.forEach((h) => h(msg));
        } catch {
          // malformed frame — drop this line only; backend logs its side
        }
      }
    };

    ws.onclose = (e) => {
      if (this.closed || e.code === 1000) return;
      // Code 4001 = backend rejected with 401 (ticket invalid) — don't retry
      if (e.code === 4001) {
        this.authErrorHandlers.forEach((h) => h("connection rejected by server (4001)"));
        return;
      }
      this._scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose fires after onerror; reconnect handled there
    };
  }

  private _drain(): void {
    const queued = this.queue.splice(0);
    queued.forEach((m) => this.send(m));
  }

  private _scheduleReconnect(): void {
    this._clearTimer();
    const delay = Math.min(BACKOFF_MAX, BACKOFF_BASE * 2 ** this.retryCount);
    this.retryCount += 1;
    this.retryTimer = setTimeout(() => {
      void this._open();
    }, delay);
  }

  private _clearTimer(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}

export const wsService = new WebSocketManager();
